import {
  AlertTriangle,
  Battery,
  BookOpen,
  Calculator,
  ChevronRight,
  Cpu,
  FileText,
  Folder,
  Globe,
  Mail,
  Maximize2,
  Monitor,
  Terminal,
  RotateCw,
  Search,
  Settings,
  Shield,
  Trash2,
  Volume2,
  Wifi,
  X
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Clock from './Clock';
import WindowComponent from './WindowComponent';
import WindowContent from './WindowContent';
import StartMenu from './StartMenu';
import NotificationPopup from './NotificationPopup';
import { normalizePath, getParentPath, joinPath, getFileName } from '../utils/pathUtils';
import { Windows11IconMap } from './Windows11Icons';

const RealisticOSSimulator = ({
  osType = 'windows11',
  scenarios = [],
  scenario,
  onComplete,
  onExit,
  fullScreen = true,
  challenge,
  onExecuteFile,
  onOpenOutlook,
  onOpenTaskManager,
  onEndProcess,
  onOpenNotepad,
  activeStep
}) => {
  // Removed currentTime state - now handled by separate Clock component
  const [activeWindows, setActiveWindows] = useState([]); // Window manager
  const [processes, setProcesses] = useState([]); // Process list (includes window processes + background)
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [taskbarApps, setTaskbarApps] = useState([]);
  const [desktopIcons, setDesktopIcons] = useState([]);
  const [wallpaper, setWallpaper] = useState('/wallpaper.png');
  const [systemTrayExpanded, setSystemTrayExpanded] = useState(false);
  const [rightClickMenu, setRightClickMenu] = useState(null);
  const [draggedWindow, setDraggedWindow] = useState(null);
  const [systemSounds, setSystemSounds] = useState(true);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [bootSequence, setBootSequence] = useState('');
  const [showChallengeBriefing, setShowChallengeBriefing] = useState(false); // New state for briefing
  const [fileSystem, setFileSystem] = useState([]); // Shared file system state
  const audioRef = useRef(null);
  const navigate = useNavigate();

  // Z-index management - start from a reasonable base and increment
  const zIndexCounter = useRef(1000);
  const mergedScenarioRef = useRef(null);

  const getNextZIndex = useCallback(() => {
    zIndexCounter.current += 1;
    return zIndexCounter.current;
  }, []);

  // Realistic system specifications
  const systemSpecs = {
    username: 'John Anderson',
    computerName: 'DESKTOP-5K2M8XP',
    domain: 'CONTOSO.COM',
    os: 'Windows 11 Pro',
    version: '22H2',
    build: '22621.2715',
    processor: 'Intel(R) Core(TM) i7-12700K CPU @ 3.60GHz',
    installedRAM: '32.0 GB',
    systemType: '64-bit operating system, x64-based processor',

    // System resource limits
    totalMemoryMB: 32768, // 32 GB in MB
    totalCPUCores: 12,    // i7-12700K has 12 cores
    maxDiskIOmbps: 500,   // Max disk I/O in MB/s
    maxNetworkMbps: 1000  // Max network speed in Mbps (1 Gbps)
  };

  // Boot sequence simulation
  useEffect(() => {
    if (fullScreen) {
      setIsLoading(true);
      const bootSteps = [
        'Initializing system...',
        'Preparing desktop...',
        'Welcome'
      ];

      let step = 0;
      const bootInterval = setInterval(() => {
        if (step < bootSteps.length) {
          setBootSequence(bootSteps[step]);
          step++;
        } else {
          clearInterval(bootInterval);
          setIsLoading(false);
          playSystemSound('startup');
          if (scenario?.description) { // Show briefing after boot if scenario has description
            setShowChallengeBriefing(true);
          }
        }
      }, 300);

      return () => clearInterval(bootInterval);
    } else {
      setIsLoading(false);
    }
  }, [fullScreen, scenario?.description]); // Added scenario.description to dependencies

  // Initialize file system once
  useEffect(() => {
    const baseFS = [
      { id: 'this-pc', name: 'This PC', type: 'computer', path: 'This PC', parentPath: null },
      { id: 'c-drive', name: 'Local Disk (C:)', type: 'drive', path: 'C:\\', parentPath: 'This PC' },
      { id: 'e-drive', name: 'Removable Disk (E:)', type: 'drive', path: 'E:\\', parentPath: 'This PC' },
      { id: 'c-users', name: 'Users', type: 'folder', path: 'C:\\Users', parentPath: 'C:\\' },
      { id: 'c-windows', name: 'Windows', type: 'folder', path: 'C:\\Windows', parentPath: 'C:\\' },
      { id: 'c-program', name: 'Program Files', type: 'folder', path: 'C:\\Program Files', parentPath: 'C:\\' },
      { id: 'c-users-john', name: 'John Anderson', type: 'folder', path: 'C:\\Users\\John Anderson', parentPath: 'C:\\Users' },
      { id: 'c-users-john-docs', name: 'Documents', type: 'folder', path: 'C:\\Users\\John Anderson\\Documents', parentPath: 'C:\\Users\\John Anderson' },
      { id: 'c-users-john-downloads', name: 'Downloads', type: 'folder', path: 'C:\\Users\\John Anderson\\Downloads', parentPath: 'C:\\Users\\John Anderson' },
      { id: 'c-users-john-docs-report', name: 'Report.docx', type: 'file', size: '1.2 MB', modified: '2024-07-20', path: 'C:\\Users\\John Anderson\\Documents\\Report.docx', parentPath: 'C:\\Users\\John Anderson\\Documents', suspicious: false },
      { id: 'c-windows-logs', name: 'Logs', type: 'folder', path: 'C:\\Windows\\Logs', parentPath: 'C:\\Windows' },
      { id: 'c-users-john-appdata', name: 'AppData', type: 'folder', path: 'C:\\Users\\John Anderson\\AppData', parentPath: 'C:\\Users\\John Anderson' },
      { id: 'c-users-john-appdata-local', name: 'Local', type: 'folder', path: 'C:\\Users\\John Anderson\\AppData\\Local', parentPath: 'C:\\Users\\John Anderson\\AppData' },
      { id: 'c-users-john-appdata-local-temp', name: 'Temp', type: 'folder', path: 'C:\\Users\\John Anderson\\AppData\\Local\\Temp', parentPath: 'C:\\Users\\John Anderson\\AppData\\Local' },
      { id: 'c-temp', name: 'Temp', type: 'folder', path: 'C:\\Temp', parentPath: 'C:\\' },
    ];
    setFileSystem(baseFS);
  }, []); // Only run once on mount

  useEffect(() => {
    if (!scenario) {
      mergedScenarioRef.current = null;
      return;
    }

    const scenarioId = scenario.challengeId || scenario.id || scenario.title || JSON.stringify(scenario.fileSystem || []);
    if (mergedScenarioRef.current === scenarioId) {
      return;
    }

    if (!Array.isArray(scenario.fileSystem) || scenario.fileSystem.length === 0) {
      mergedScenarioRef.current = scenarioId;
      return;
    }

    // Using centralized path utilities from pathUtils.js

    setFileSystem(prev => {
      const existingPaths = new Map(prev.map(item => [item.path, item]));
      const additions = [];

      const ensureDirectory = (dirPath) => {
        const normalizedDir = normalizePath(dirPath);
        if (!normalizedDir || normalizedDir === 'This PC') {
          return;
        }

        const segments = normalizedDir.split('\\').filter(Boolean);
        if (segments.length === 0) {
          return;
        }

        let runningPath = segments[0];
        if (/^[A-Za-z]:$/.test(runningPath)) {
          runningPath = `${runningPath}\\`;
        }

        for (let i = 1; i <= segments.length; i++) {
          const nextSegment = segments[i];
          if (i === 1 && /^[A-Za-z]:/.test(segments[0])) {
            runningPath = segments[0].endsWith('\\') ? segments[0] : `${segments[0]}\\`;
          }

          if (nextSegment) {
            runningPath = joinPath(runningPath, nextSegment);
          }

          if (!existingPaths.has(runningPath) && !additions.find(item => item.path === runningPath)) {
            additions.push({
              id: `dir-${runningPath.replace(/[\\:]/g, '-')}`,
              name: nextSegment || runningPath,
              type: 'folder',
              path: runningPath,
              parentPath: getParentPath(runningPath),
              size: '',
              modified: new Date().toLocaleDateString(),
              suspicious: false,
              isSuspicious: false
            });
          }
        }
      };

      scenario.fileSystem.forEach(file => {
        if (!file || !file.name) {
          return;
        }

        const directoryPath = normalizePath(file.path || scenario.initialExplorerPath || 'C:\\Users\\John Anderson\\Downloads');
        ensureDirectory(directoryPath);

        const filePath = joinPath(directoryPath, file.name);

        if (!existingPaths.has(filePath) && !additions.find(item => item.path === filePath)) {
          additions.push({
            id: file.id || `${scenarioId}-auto-${file.name}-${Math.random().toString(36).slice(2)}`,
            name: file.name,
            type: file.type || 'file',
            path: filePath,
            parentPath: directoryPath || 'C:\\',
            size: file.size || '1 KB',
            modified: file.modified || new Date().toLocaleDateString(),
            description: file.description,
            suspicious: !!file.isSuspicious,
            isSuspicious: !!file.isSuspicious,
            hash: file.hash,
            signature: file.signature,
            processConfig: file.processConfig || null,
            autoGenerated: file.autoGenerated || false
          });
        }
      });

      if (additions.length === 0) {
        return prev;
      }

      const combined = [...prev, ...additions];
      return Array.from(new Map(combined.map(item => [item.path, item])).values());
    });

    mergedScenarioRef.current = scenarioId;
  }, [scenario]);

  // Initialize default desktop
  useEffect(() => {
    if (!isLoading) {
      setDesktopIcons([
        { id: 'documents', name: 'File Explorer', icon: 'documents', x: 50, y: 50, type: 'app', targetApp: 'explorer', initialPath: 'C:\\Users\\John Anderson\\Documents' },
        { id: 'chrome', name: 'Google Chrome', icon: 'chrome', x: 50, y: 150, type: 'app' },
        { id: 'notepad', name: 'Notepad', icon: 'notepad', x: 50, y: 250, type: 'app' },
        { id: 'cmd', name: 'Command Prompt', icon: 'cmd', x: 50, y: 350, type: 'app', targetApp: 'cmd' }
      ]);

      setTaskbarApps([
        { id: 'start', name: 'Start', icon: null, pinned: true, active: false },
        { id: 'search', name: 'Search', icon: Search, pinned: true, active: false },
        { id: 'taskview', name: 'Task View', icon: Maximize2, pinned: true, active: false },
        { id: 'explorer', name: 'File Explorer', icon: Folder, pinned: true, active: false },
        { id: 'chrome', name: 'Google Chrome', icon: Globe, pinned: true, active: false },
        { id: 'outlook', name: 'Microsoft Outlook', icon: Mail, pinned: true, active: false },
        { id: 'cmd', name: 'Command Prompt', icon: Terminal, pinned: true, active: false },
        { id: 'settings', name: 'Settings', icon: Settings, pinned: true, active: false }
      ]);

      // Initialize system background processes with mean/jitter for realistic metrics
      const systemProcesses = [
        {
          id: 'bg-system',
          name: 'System',
          type: 'background',
          cpu: { mean: 0.3, jitter: 0.1 },
          memory: { mean: 12.8, jitter: 0.5 },
          disk: { mean: 0.5, jitter: 0.2 },
          network: { mean: 0, jitter: 0 },
          isRogue: false
        },
        {
          id: 'bg-svchost-1',
          name: 'svchost.exe',
          type: 'background',
          cpu: { mean: 0.2, jitter: 0.15 },
          memory: { mean: 23.4, jitter: 1.0 },
          disk: { mean: 0.1, jitter: 0.05 },
          network: { mean: 2, jitter: 1 },
          isRogue: false
        },
        {
          id: 'bg-svchost-2',
          name: 'svchost.exe',
          type: 'background',
          cpu: { mean: 0.1, jitter: 0.08 },
          memory: { mean: 18.2, jitter: 0.8 },
          disk: { mean: 0, jitter: 0 },
          network: { mean: 1, jitter: 0.5 },
          isRogue: false
        },
        {
          id: 'bg-csrss',
          name: 'csrss.exe',
          type: 'background',
          cpu: { mean: 0.05, jitter: 0.03 },
          memory: { mean: 5.1, jitter: 0.2 },
          disk: { mean: 0, jitter: 0 },
          network: { mean: 0, jitter: 0 },
          isRogue: false
        },
      ];

      // Add scenario-specific background processes
      const scenarioProcesses = scenario?.backgroundProcesses || [];

      setProcesses([...systemProcesses, ...scenarioProcesses]);

      // Add scenario-specific elements
      if (scenario && scenario.initialNotifications) {
        setNotifications(scenario.initialNotifications);
      }
    }
  }, [isLoading, scenario]);

  useEffect(() => {
    // If the OS is NOT loading AND we have a scenario with a description, then show the briefing.
    if (!isLoading && scenario?.description) {
      setShowChallengeBriefing(true);
    }
  }, [isLoading, scenario]);

  // No CPU cycle needed here - Task Manager calculates current values when it's open

  const playSystemSound = useCallback((soundType) => {
    if (!systemSounds) return;

    // In a real implementation, you'd load actual Windows sounds
    const sounds = {
      startup: '/sounds/windows-startup.wav',
      click: '/sounds/click.wav',
      error: '/sounds/error.wav',
      notification: '/sounds/notification.wav',
      shutdown: '/sounds/shutdown.wav'
    };

    if (audioRef.current) {
      audioRef.current.src = sounds[soundType] || sounds.click;
      audioRef.current.play().catch(() => { }); // Ignore autoplay restrictions
    }
  }, [systemSounds]);

  const handleLearnMore = () => {
    const meta = challenge?.metadata;

    // Support older challenges where metadata was a plain URL string
    if (typeof meta === 'string' && meta.startsWith('http')) {
      window.open(meta, '_blank', 'noopener,noreferrer');
      return;
    }

    // Support newer JSON metadata objects with explicit URLs
    if (meta && typeof meta === 'object') {
      const url =
        meta.learnMoreUrl ||
        meta.learn_more_url ||
        meta.docUrl ||
        meta.doc_url ||
        meta.url;

      if (typeof url === 'string' && url.startsWith('http')) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
    }

    // Fallback to arena-based navigation
    const arenaId = scenario?.arenaId || 'os_security'; // Fallback
    if (arenaId) {
      navigate(`/learning/module/${arenaId}?topic=${arenaId}`);
    }
  };

  const getAppDimensions = (appId) => {
    const dimensions = {
      calculator: { width: 320, height: 520 },
      notepad: { width: 700, height: 600 },
      taskmgr: { width: 900, height: 650 },
      outlook: { width: 900, height: 700 },
      cmd: { width: 780, height: 520 },
    };
    return dimensions[appId] || { width: 800, height: 500 };
  };

  // Handle ending a process - removes from process list and closes associated window
  const handleEndTask = useCallback((processId) => {
    // Access process info before removal by capturing it in the setter
    let processInfo = null;

    // Remove from process list and capture the process info
    setProcesses(prev => {
      processInfo = prev.find(p => p.id === processId);
      return prev.filter(p => p.id !== processId);
    });

    if (!processInfo) return;

    // If it's a window process, also close the window
    if (processInfo.type === 'window') {
      setActiveWindows(prev => {
        const remaining = prev.filter(w => w.id !== processId);

        // Update taskbar based on remaining windows
        const hasOtherWindows = remaining.some(w => w.appId === processInfo.appId);
        setTaskbarApps(prevApps => prevApps.map(app =>
          app.id === processInfo.appId ? { ...app, active: hasOtherWindows } : app
        ));

        return remaining;
      });

      playSystemSound('close');
    }

    // Notify parent component about process end
    if (onEndProcess) {
      onEndProcess(processId, processInfo.name);
    }

    // Check if it was a rogue process (scenario completion)
    if (processInfo.isRogue && scenario?.onEndRogueProcess) {
      scenario.onEndRogueProcess(processId);
    }
  }, [scenario, onEndProcess]);

  const bringToFront = useCallback((windowId) => {
    setActiveWindows(prev => {
      // Find the current max z-index
      const maxZIndex = Math.max(...prev.map(w => w.zIndex));
      const targetWindow = prev.find(w => w.id === windowId);

      // Only update if the window is not already at the front
      if (targetWindow && targetWindow.zIndex < maxZIndex) {
        return prev.map(w =>
          w.id === windowId ? { ...w, zIndex: getNextZIndex() } : w
        );
      }

      // Return unchanged array if window is already at front (prevents re-render)
      return prev;
    });
  }, [getNextZIndex]);

  const openWindow = useCallback((appId, windowData = {}) => { // windowData can now include initialPath
    playSystemSound('click');

    // Check if a window of this app type already exists (prevent duplicates)
    // Use functional update to check and handle existing window
    let shouldCreateNewWindow = true;
    setActiveWindows(prev => {
      const existingWindow = prev.find(w => w.appId === appId && !w.isMinimized);
      if (existingWindow) {
        // Bring existing window to front instead of creating duplicate
        shouldCreateNewWindow = false;
        // We'll call bringToFront after the state update completes
        setTimeout(() => bringToFront(existingWindow.id), 0);
        return prev; // Don't add new window
      }
      // Window doesn't exist, we'll create it below
      return prev;
    });

    // If window already exists, don't create a new one
    // Note: Due to React's batching, we also check again in the final setActiveWindows
    if (!shouldCreateNewWindow) {
      return;
    }

    const defaultDimensions = getAppDimensions(appId);
    const windowId = `${appId}-${Date.now()}`;
    const derivedInitialPath = windowData.initialPath ?? (appId === 'explorer' && scenario?.initialExplorerPath ? scenario.initialExplorerPath : undefined);

    // Create window entry
    const newWindow = {
      id: windowId,
      appId,
      title: windowData.title || getAppTitle(appId),
      icon: windowData.icon || getAppIcon(appId),
      x: windowData.x || Math.random() * 200 + 100,
      y: windowData.y || Math.random() * 100 + 100,
      width: windowData.width || defaultDimensions.width,
      height: windowData.height || defaultDimensions.height,
      isMaximized: windowData.isMaximized || false,
      isMinimized: false,
      zIndex: getNextZIndex(),
      // Store data instead of React elements
      initialPath: derivedInitialPath,
      resizable: windowData.resizable !== false,
      ...windowData
    };

    // Define process metrics based on app type
    const getProcessMetrics = (appId) => {
      // Default: light application
      const defaults = {
        cpu: { mean: 0.4, jitter: 0.2 },        // 0.4% CPU
        memory: { mean: 30, jitter: 5 },         // 30 MB
        disk: { mean: 0.5, jitter: 0.2 },        // 0.5 MB/s
        network: { mean: 0.1, jitter: 0.05 }     // 0.1 Mbps
      };

      // Only specify apps that differ significantly from defaults
      const customMetrics = {
        chrome: {
          cpu: { mean: 3.5, jitter: 1.5 },
          memory: { mean: 521, jitter: 50 },
          disk: { mean: 2, jitter: 1 },
          network: { mean: 15, jitter: 10 }      // Active browser
        },
        explorer: {
          cpu: { mean: 1.2, jitter: 0.5 },
          memory: { mean: 146, jitter: 15 },
          disk: { mean: 5, jitter: 3 },          // File operations
          network: { mean: 0.5, jitter: 0.3 }
        },
        taskmgr: {
          cpu: { mean: 2.5, jitter: 0.8 },
          memory: { mean: 52, jitter: 10 },
          disk: { mean: 0.2, jitter: 0.1 },
          network: { mean: 0, jitter: 0 }
        },
        outlook: {
          cpu: { mean: 1.8, jitter: 0.6 },
          memory: { mean: 215, jitter: 30 },
          disk: { mean: 1, jitter: 0.5 },
          network: { mean: 3, jitter: 2 }         // Email sync
        },
        notepad: {
          cpu: { mean: 0.1, jitter: 0.05 },
          memory: { mean: 12, jitter: 2 },
          disk: { mean: 0, jitter: 0 },
          network: { mean: 0, jitter: 0 }
        },
      };

      return customMetrics[appId] || defaults;
    };

    const processMetrics = getProcessMetrics(appId);

    // Create process entry (only mean/jitter, no current values)
    const newProcess = {
      id: windowId, // Same ID as window for linking
      name: `${newWindow.title}.exe`,
      type: 'window',
      appId,
      cpu: { ...processMetrics.cpu },
      memory: { ...processMetrics.memory },
      disk: { ...processMetrics.disk },
      network: { ...processMetrics.network },
      isRogue: windowData.isRogue || false,
      windowId: windowId
    };

    setActiveWindows(prev => {
      // Double-check for existing window before adding
      const existingWindow = prev.find(w => w.appId === appId && !w.isMinimized);
      if (existingWindow) {
        // Bring existing window to front instead of creating duplicate
        setTimeout(() => bringToFront(existingWindow.id), 0);
        return prev;
      }
      return [...prev, newWindow];
    });
    setProcesses(prev => {
      // Check if process already exists (same appId)
      const existingProcess = prev.find(p => p.appId === appId && p.windowId);
      if (existingProcess) {
        return prev;
      }
      return [...prev, newProcess];
    });

    // Update taskbar
    setTaskbarApps(prev => prev.map(app =>
      app.id === appId ? { ...app, active: true } : app
    ));

    // Notify callbacks for specific apps
    if (appId === 'outlook' && onOpenOutlook) {
      onOpenOutlook();
    }
    if (appId === 'taskmgr' && onOpenTaskManager) {
      onOpenTaskManager();
    }
    if (appId === 'notepad' && onOpenNotepad) {
      onOpenNotepad(newWindow);
    }
    // Emit browser_opened event when chrome window is opened
    if (appId === 'chrome' && onComplete) {
      onComplete('browser_opened', { windowId, timestamp: Date.now() });
    }

    // Special handling for polymorphic virus scenario
    const challengeId = challenge?.id || challenge?._id || challenge?.challenge_id;
    if (challengeId === '066306f2-2fb1-4b7f-bb81-e6f7b837975f' && appId === 'notepad') {
      // Trigger Task Manager opening after a delay
      setTimeout(() => {
        openWindow('taskmgr');
      }, 2000);
    }
  }, [onOpenOutlook, onOpenTaskManager, onOpenNotepad, challenge, bringToFront]);

  const closeWindow = useCallback((windowId) => {
    playSystemSound('click');

    // Remove from window manager
    setActiveWindows(prev => {
      const remaining = prev.filter(w => w.id !== windowId);
      const closedWindow = prev.find(w => w.id === windowId);

      if (closedWindow) {
        // Update taskbar if no other windows of same app
        const hasOtherWindows = remaining.some(w => w.appId === closedWindow.appId);
        if (!hasOtherWindows) {
          setTaskbarApps(prevApps => prevApps.map(app =>
            app.id === closedWindow.appId ? { ...app, active: false } : app
          ));
        }
      }

      return remaining;
    });

    // Remove from process list
    setProcesses(prev => prev.filter(p => p.id !== windowId));
  }, []);

  const maximizeWindow = useCallback((windowId) => {
    playSystemSound('click');
    setActiveWindows(prev => prev.map(w =>
      w.id === windowId
        ? { ...w, isMaximized: !w.isMaximized, x: w.isMaximized ? 100 : 0, y: w.isMaximized ? 100 : 0 }
        : w
    ));
  }, []);

  const minimizeWindow = useCallback((windowId) => {
    playSystemSound('click');
    setActiveWindows(prev => prev.map(w =>
      w.id === windowId ? { ...w, isMinimized: true } : w
    ));
  }, []);

  const updateWindow = useCallback((windowId, updates) => {
    setActiveWindows(prev => prev.map(w =>
      w.id === windowId ? { ...w, ...updates } : w
    ));
  }, []);

  // Notification management
  const addNotification = useCallback((type, title, message) => {
    const newNotification = {
      id: `notif-${Date.now()}-${Math.random()}`,
      type,
      title,
      message
    };

    setNotifications(prev => [...prev, newNotification]);

    return newNotification;
  }, []);

  // Track which processes have been spawned from steps
  const spawnedProcessesRef = useRef(new Set());

  // Handler to spawn processes when macro is enabled
  const spawnMacroProcesses = useCallback(() => {
    if (!activeStep?.content?.scenario?.backgroundProcesses) return;

    const stepProcesses = activeStep.content.scenario.backgroundProcesses || [];
    if (stepProcesses.length === 0) return;

    console.log('[OS] Spawning macro processes:', stepProcesses.map(p => p.name || p.id));

    // Add processes that don't already exist and haven't been spawned yet
    setProcesses(prev => {
      const existingIds = new Set(prev.map(p => p.id));
      const newProcesses = stepProcesses
        .filter(proc => proc.id && !existingIds.has(proc.id) && !spawnedProcessesRef.current.has(proc.id))
        .map(proc => {
          // Mark as spawned
          spawnedProcessesRef.current.add(proc.id);
          return {
            id: proc.id,
            name: proc.name || proc.id,
            type: proc.type || 'background',
            cpu: proc.cpu || { mean: 0, jitter: 0 },
            memory: proc.memory || { mean: 0, jitter: 0 },
            disk: proc.disk || { mean: 0, jitter: 0 },
            network: proc.network || { mean: 0, jitter: 0 },
            isRogue: proc.isRogue ?? true,
            cmdline: proc.cmdline || null,
            spawnedBy: proc.spawnedBy || null
          };
        });

      if (newProcesses.length === 0) return prev;

      // Show notification if provided
      if (activeStep?.content?.notify) {
        const notify = activeStep.content.notify;
        addNotification(
          notify.type || 'alert',
          notify.title || 'Macro Executed',
          notify.message || 'Macro executed: downloader retrieved and executed payload.'
        );
      }

      return [...prev, ...newProcesses];
    });
  }, [activeStep, addNotification]);

  // Wrapper for onComplete that handles macro_enabled events
  const handleOSEvent = useCallback((event, payload) => {
    // If macro_enabled event is received, spawn processes
    if (event === 'macro_enabled') {
      console.log('[OS] Macro enabled event received, spawning processes...');
      spawnMacroProcesses();
    }

    // Forward event to parent
    if (onComplete) {
      onComplete(event, payload);
    }
  }, [onComplete, spawnMacroProcesses]);

  const handleCommandExecuted = useCallback((result = {}) => {
    if (!result || !onComplete) {
      if (onComplete) {
        onComplete('command_executed', result);
      }
      return;
    }

    if (result.matchesExpected) {
      const discovery = result.onDiscovery || {};
      const normalizedAdsPath = result.discoveries?.adsPath ? normalizePath(result.discoveries.adsPath) : null;

      if (discovery.notify) {
        const notificationConfig = discovery.notify;
        addNotification(
          notificationConfig.type || 'info',
          notificationConfig.title || 'Command completed',
          notificationConfig.message || ''
        );
      }

      if (discovery.spawnedProcess) {
        const proc = discovery.spawnedProcess;
        const processId = proc.id || `cmd-proc-${Date.now()}`;

        const coerceMetric = (metric) => {
          if (metric && typeof metric.mean === 'number' && typeof metric.jitter === 'number') {
            return metric;
          }
          return { mean: 0, jitter: 0 };
        };

        setProcesses(prev => {
          if (prev.some(p => p.id === processId)) {
            return prev;
          }

          const newProcess = {
            id: processId,
            name: proc.name || processId,
            type: 'background',
            cpu: coerceMetric(proc.cpu),
            memory: coerceMetric(proc.memory),
            disk: coerceMetric(proc.disk),
            network: coerceMetric(proc.network),
            isRogue: proc.isRogue ?? true,
            source: 'command_prompt',
            sourceCommand: result.command,
            sourceFileId: proc.sourceFileId || null,
            sourceAdsPath: normalizedAdsPath
          };

          return [...prev, newProcess];
        });
      }
    }

    onComplete('command_executed', result);
  }, [addNotification, onComplete]);

  const handleExplorerQuarantine = useCallback((item = {}) => {
    const isAdsStream = item?.type === 'ads_stream' || (item?.name || '').includes(':');

    if (!isAdsStream) {
      return { handledEvent: false };
    }

    const normalizedAdsPath = item?.path ? normalizePath(item.path) : null;
    let terminatedProcess = null;

    setProcesses(prev => {
      const remaining = prev.filter(process => {
        const matchesAdsPath = normalizedAdsPath && process.sourceAdsPath
          ? normalizePath(process.sourceAdsPath) === normalizedAdsPath
          : false;

        const matchesProcessId = (activeStep?.content?.targets || []).some(target => {
          const targetProcessId = target.processId || target.id || target?.process_id;
          return targetProcessId && process.id === targetProcessId;
        });

        if (!terminatedProcess && (matchesAdsPath || matchesProcessId)) {
          terminatedProcess = process;
          return false;
        }

        return true;
      });

      return remaining;
    });

    if (terminatedProcess) {
      addNotification(
        'success',
        'Process terminated',
        `${terminatedProcess.name} has been stopped.`
      );
    }

    const notifyConfig = activeStep?.content?.onCompleteNotify;
    if (notifyConfig) {
      addNotification(
        notifyConfig.type || 'success',
        notifyConfig.title || 'Quarantine Complete',
        notifyConfig.message || ''
      );
    }

    if (onComplete) {
      onComplete('ads_quarantined', {
        file: {
          id: item.id,
          name: item.name,
          path: normalizedAdsPath || item.path,
          type: item.type,
          size: item.size,
          description: item.description,
          suspicious: item.suspicious || item.isSuspicious
        },
        adsPath: normalizedAdsPath || item.path,
        processTerminated: !!terminatedProcess,
        terminatedProcess: terminatedProcess
          ? {
            id: terminatedProcess.id,
            name: terminatedProcess.name
          }
          : null,
        quarantineLocation: activeStep?.content?.expectedOutcome?.quarantineLocation
      });
    }

    return {
      handledEvent: true,
      payload: {
        event: 'ads_quarantined',
        payload: {
          file: {
            id: item.id,
            name: item.name,
            path: normalizedAdsPath || item.path,
            type: item.type,
            size: item.size,
            description: item.description,
            suspicious: item.suspicious || item.isSuspicious
          },
          adsPath: normalizedAdsPath || item.path,
          processTerminated: !!terminatedProcess,
          terminatedProcess: terminatedProcess
            ? {
              id: terminatedProcess.id,
              name: terminatedProcess.name
            }
            : null,
          quarantineLocation: activeStep?.content?.expectedOutcome?.quarantineLocation
        }
      }
    };
  }, [activeStep, addNotification, onComplete]);

  // File system management
  const addFileToDownloads = useCallback((fileName, fileSize = '1 KB', fileType = 'file', metadata = {}) => {
    const downloadsPath = 'C:\\Users\\John Anderson\\Downloads';
    const newFile = {
      id: `c-users-john-downloads-${Date.now()}-${Math.random()}`,
      name: fileName,
      type: fileType,
      size: fileSize,
      modified: new Date().toLocaleDateString(),
      path: `${downloadsPath}\\${fileName}`,
      parentPath: downloadsPath,
      suspicious: metadata.isMalicious || false, // Mark as suspicious if malicious
      // Preserve metadata like processConfig, isMalicious, etc.
      ...metadata
    };

    console.log('[OS] Adding file to downloads:', newFile);

    setFileSystem(prev => {
      // Check if file already exists
      const exists = prev.some(f => f.path === newFile.path);
      if (exists) {
        // Replace existing file
        return prev.map(f => f.path === newFile.path ? newFile : f);
      } else {
        // Add new file
        return [...prev, newFile];
      }
    });

    return newFile;
  }, []);

  // File execution handler
  const handleFileExecute = useCallback((file) => {
    console.log('[OS] Executing file:', file.name, 'Full file object:', file);

    // Check if file has process config (malware)
    if (file.processConfig) {
      const processId = `malware-${Date.now()}`;
      const malwareProcess = {
        id: processId,
        name: file.processConfig.name,
        type: 'background',
        cpu: { ...file.processConfig.cpu },
        memory: { ...file.processConfig.memory },
        disk: { ...file.processConfig.disk },
        network: { ...file.processConfig.network },
        isRogue: file.processConfig.isRogue || true
      };

      console.log('[OS] Starting malware process:', malwareProcess);
      setProcesses(prev => {
        console.log('[OS] Current processes before adding malware:', prev.length);
        const newProcesses = [...prev, malwareProcess];
        console.log('[OS] Processes after adding malware:', newProcesses.length, newProcesses.map(p => p.name));
        return newProcesses;
      });

      // Show notification if step has notify config
      if (activeStep?.content?.notify) {
        const notifyConfig = activeStep.content.notify;
        const title = notifyConfig.title || 'Process Started';
        const message = notifyConfig.message || `A new process "${malwareProcess.name}" has started.`;
        addNotification('warning', title, message);
        console.log('[OS] Notification shown for rogue process:', title, message);
      } else if (malwareProcess.isRogue) {
        // Default notification for rogue processes
        addNotification('warning', 'Rogue Process Detected',
          `A suspicious process "${malwareProcess.name}" has started. This may be malware activity.`);
      }

    } else {
      console.log('[OS] File has no processConfig, not starting any process');
    }

    // Notify parent component
    if (onExecuteFile) {
      onExecuteFile(file);
    }
  }, [onExecuteFile, addNotification, activeStep]);

  const handleExplorerFolderOpen = useCallback((folder) => {
    if (!onComplete) {
      return;
    }

    if (!folder) {
      onComplete('folder_opened', { path: null });
      return;
    }

    const normalizedPath = normalizePath(folder.path || folder);
    const payload = typeof folder === 'object' && folder !== null
      ? { ...folder, path: normalizedPath }
      : {
        path: normalizedPath,
        name: getFileName(normalizedPath) || normalizedPath.split('\\').filter(Boolean).pop() || normalizedPath,
        type: 'folder'
      };

    onComplete('folder_opened', payload);
  }, [onComplete]);

  const getAppTitle = (appId) => {
    const titles = {
      explorer: 'File Explorer',
      chrome: 'Google Chrome',
      notepad: 'Untitled - Notepad',
      settings: 'Settings',
      calculator: 'Calculator',
      outlook: 'Microsoft Outlook',
      securitycenter: 'Windows Security',
      controlpanel: 'Control Panel',
      devicemanager: 'Device Manager',
      eventviewer: 'Event Viewer',
      services: 'Services',
      regedit: 'Registry Editor',
      cmd: 'Command Prompt',
      powershell: 'Windows PowerShell',
      taskmgr: 'Task Manager'
    };
    return titles[appId] || 'Application';
  };

  const getAppIcon = (appId) => {
    const icons = {
      explorer: Folder,
      chrome: Globe,
      notepad: FileText,
      settings: Settings,
      calculator: Calculator,
      outlook: Mail,
      securitycenter: Shield,
      controlpanel: Settings,
      devicemanager: Monitor,
      eventviewer: FileText,
      services: Settings,
      regedit: Settings,
      cmd: Terminal,
      powershell: Monitor,
      taskmgr: Cpu
    };
    return icons[appId] || Monitor;
  };

  const handleRightClick = useCallback((e, context = 'desktop') => {
    e.preventDefault();
    setRightClickMenu({
      x: e.clientX,
      y: e.clientY,
      context,
      items: getRightClickItems(context)
    });
  }, []);

  const getRightClickItems = (context) => {
    switch (context) {
      case 'desktop':
        return [
          { label: 'View', submenu: ['Large icons', 'Medium icons', 'Small icons'] },
          { label: 'Sort by', submenu: ['Name', 'Date modified', 'Type', 'Size'] },
          { label: 'Refresh', icon: RotateCw, action: () => setRightClickMenu(null) },
          { label: 'Paste', disabled: true },
          { label: 'New', submenu: ['Folder', 'Shortcut', 'Text Document'] },
          { label: 'Display settings', icon: Monitor },
          { label: 'Personalize', icon: Settings }
        ];
      default:
        return [];
    }
  };

  // Boot screen
  if (isLoading && fullScreen) {
    return (
      <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center z-[9999]">
        <div className="mb-8">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div className="text-xl font-light">{bootSequence}</div>
        <div className="mt-4 text-sm text-gray-400">Windows 11</div>
      </div>
    );
  }

  return (
    <>
      <audio ref={audioRef} preload="auto" />

      <div
        className={`${fullScreen ? 'fixed inset-0 z-[9998]' : 'relative w-full h-full'} bg-cover bg-center bg-no-repeat select-none`}
        style={{
          backgroundImage: `url(${wallpaper})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#1e3a8a', // Fallback color if image fails to load
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
        onContextMenu={handleRightClick}
        onClick={() => {
          setRightClickMenu(null);
          setStartMenuOpen(false);
        }}
      >
        {/* Windows 11 Style Desktop Icons - Clean Icons with Text Labels */}
        <div className="absolute inset-0 p-2 sm:p-4">
          {desktopIcons.map(icon => {
            // Get Windows 11 icon component or fallback to Lucide icon
            const IconComponent = typeof icon.icon === 'string'
              ? (Windows11IconMap[icon.icon] || Monitor)
              : icon.icon;

            return (
              <div
                key={icon.id}
                className="absolute flex flex-col items-center cursor-pointer group"
                style={{
                  left: icon.x,
                  top: icon.y,
                  width: window.innerWidth < 640 ? '70px' : '90px'
                }}
                onDoubleClick={() => openWindow(icon.targetApp || icon.id, { initialPath: icon.initialPath })}
              >
                {/* Icon - Filled colorful icons, directly on wallpaper */}
                <div
                  className="flex items-center justify-center mb-1.5 transition-transform duration-200 group-hover:scale-110"
                  style={{
                    filter: 'drop-shadow(0 3px 6px rgba(0, 0, 0, 0.4)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3)) drop-shadow(0 0 8px rgba(255, 255, 255, 0.1))'
                  }}
                >
                  <IconComponent
                    size={window.innerWidth < 640 ? 42 : 56}
                    className="transition-all duration-200"
                  />
                </div>
                {/* Text Label - White text with shadow below icon */}
                <span
                  className="text-xs text-white text-center leading-tight px-1 font-normal"
                  style={{
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.5), 0 1px 1px rgba(0, 0, 0, 0.3)',
                    wordBreak: 'break-word',
                    maxWidth: '90px'
                  }}
                >
                  {icon.name}
                </span>
              </div>
            );
          })}
        </div>

        {/* Active Windows */}
        {activeWindows.map(window => {
          // Function body ensures proper closure capture of processes state
          return (
            <WindowComponent
              key={window.id}
              window={window}
              onUpdateWindow={updateWindow}
              onBringToFront={bringToFront}
              onMinimize={minimizeWindow}
              onMaximize={maximizeWindow}
              onClose={closeWindow}
            >
              <WindowContent
                windowId={window.id}
                appId={window.appId}
                initialPath={window.initialPath}
                scenario={scenario}
                onComplete={handleOSEvent}
                activeStep={activeStep}
                processes={window.appId === 'taskmgr' ? processes : undefined}
                onEndTask={window.appId === 'taskmgr' ? handleEndTask : undefined}
                systemSpecs={window.appId === 'taskmgr' ? systemSpecs : undefined}
                fileSystem={window.appId === 'explorer' ? fileSystem : undefined}
                setFileSystem={window.appId === 'explorer' ? setFileSystem : undefined}
                addFileToDownloads={window.appId === 'outlook' ? addFileToDownloads : undefined}
                addNotification={window.appId === 'outlook' ? addNotification : undefined}
                onExecuteFile={window.appId === 'explorer' ? handleFileExecute : undefined}
                onOpenFolder={window.appId === 'explorer' ? handleExplorerFolderOpen : undefined}
                onQuarantine={window.appId === 'explorer' ? handleExplorerQuarantine : undefined}
                onExecuteCommand={window.appId === 'cmd' ? handleCommandExecuted : undefined}
                networkFlows={window.appId === 'taskmgr' ? (scenario?.networkFlows || []) : undefined}
              />
            </WindowComponent>
          );
        })}

        {/* Right Click Menu */}
        {rightClickMenu && (
          <div
            className="absolute bg-white border border-gray-300 rounded shadow-lg py-1 z-[10001]"
            style={{ left: rightClickMenu.x, top: rightClickMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            {rightClickMenu.items.map((item, index) => (
              <div
                key={index}
                className="px-4 py-2 text-sm text-gray-800 hover:bg-blue-500 hover:text-white cursor-pointer flex items-center justify-between transition-colors"
                onClick={() => {
                  item.action?.();
                  setRightClickMenu(null);
                }}
              >
                <span>{item.label}</span>
                {item.submenu && <ChevronRight size={14} className="text-gray-500" />}
              </div>
            ))}
          </div>
        )}

        {/* Windows 11 Style Taskbar - Full Width at Bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-gray-900/80 backdrop-blur-xl border-t border-white/10 h-12 flex items-center px-2 z-[9999] overflow-hidden">
          {/* Start Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setStartMenuOpen(!startMenuOpen);
            }}
            className={`w-10 h-10 rounded flex items-center justify-center transition-all duration-200 ${startMenuOpen ? 'bg-white/20' : 'hover:bg-white/10'
              }`}
          >
            <div className="w-5 h-5 bg-gradient-to-br from-blue-400 to-blue-600 rounded-sm flex items-center justify-center">
              <div className="w-3 h-3 bg-white rounded-sm"></div>
            </div>
          </button>

          {/* Search Bar */}
          <div className="hidden sm:flex items-center bg-white/10 rounded-full px-4 py-1.5 ml-2 mr-2 min-w-48 hover:bg-white/15 transition-colors cursor-pointer">
            <Search size={16} className="text-white/70 mr-2" />
            <span className="text-white/70 text-sm">Type here to search</span>
          </div>

          {/* Task View */}
          <button className="hidden sm:flex w-10 h-10 rounded items-center justify-center hover:bg-white/10 transition-colors mr-1">
            <Maximize2 size={18} className="text-white/80" />
          </button>

          {/* Taskbar Apps - Centered */}
          <div className="flex items-center space-x-1 flex-1 justify-start sm:justify-center overflow-x-auto">
            {taskbarApps.slice(3).map(app => {
              const IconComponent = app.icon;
              return (
                <button
                  key={app.id}
                  onClick={() => {
                    if (app.active) {
                      // Show/hide existing windows
                      const appWindows = activeWindows.filter(w => w.appId === app.id);
                      if (appWindows.length > 0) {
                        // Toggle minimize state
                        const allMinimized = appWindows.every(w => w.isMinimized);
                        if (allMinimized) {
                          setActiveWindows(prev => prev.map(w =>
                            w.appId === app.id ? { ...w, isMinimized: false, zIndex: getNextZIndex() } : w
                          ));
                        } else {
                          setActiveWindows(prev => prev.map(w =>
                            w.appId === app.id ? { ...w, isMinimized: true } : w
                          ));
                        }
                      }
                    } else {
                      openWindow(app.id);
                    }
                  }}
                  className={`w-10 h-10 rounded flex items-center justify-center transition-all duration-200 ${app.active
                    ? 'bg-white/20'
                    : 'hover:bg-white/10'
                    }`}
                  title={app.name}
                >
                  <IconComponent size={20} className="text-white/90" />
                </button>
              );
            })}
          </div>

          {/* System Tray */}
          <div className="hidden sm:flex items-center space-x-1 ml-auto mr-2">
            <button
              onClick={() => setSystemTrayExpanded(!systemTrayExpanded)}
              className="p-1.5 hover:bg-white/10 rounded transition-colors"
            >
              <ChevronRight
                size={12}
                className={`text-white/60 transition-transform ${systemTrayExpanded ? 'rotate-90' : ''}`}
              />
            </button>

            {systemTrayExpanded && (
              <div className="flex items-center space-x-1 px-1">
                <div className="p-1.5 hover:bg-white/10 rounded transition-colors cursor-pointer">
                  <Wifi size={16} className="text-white/80" />
                </div>
                <div className="p-1.5 hover:bg-white/10 rounded transition-colors cursor-pointer">
                  <Volume2 size={16} className="text-white/80" />
                </div>
                <div className="p-1.5 hover:bg-white/10 rounded transition-colors cursor-pointer">
                  <Shield size={16} className="text-green-400" />
                </div>
                <div className="p-1.5 hover:bg-white/10 rounded transition-colors cursor-pointer">
                  <AlertTriangle size={16} className="text-yellow-400" />
                </div>
              </div>
            )}

            <div className="p-1.5 hover:bg-white/10 rounded transition-colors cursor-pointer">
              <Battery size={16} className="text-white/80" />
            </div>
          </div>

          {/* Clock */}
          <div className="px-2 py-1 hover:bg-white/10 rounded transition-colors cursor-pointer mr-1">
            <Clock className="text-white text-xs text-right leading-tight" />
          </div>

          {/* Notification/Copilot */}
          <button className="w-10 h-10 rounded flex items-center justify-center hover:bg-white/10 transition-colors">
            <div className="w-5 h-5 bg-gradient-to-br from-purple-400 to-blue-500 rounded flex items-center justify-center">
              <div className="w-3 h-3 bg-white rounded-sm"></div>
            </div>
          </button>
        </div>

        {/* Start Menu */}
        {startMenuOpen && (
          <StartMenu
            onAppClick={(appId, initialPath) => { // Pass initialPath from StartMenu
              openWindow(appId);
              setStartMenuOpen(false);
            }}
            onClose={() => setStartMenuOpen(false)}
            systemSpecs={systemSpecs}
          />
        )}

        {/* Challenge Briefing Modal - Disabled for browser extension challenges */}
        {/* {showChallengeBriefing && scenario && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg p-4 sm:p-8 max-w-2xl w-full mx-0 sm:mx-4 shadow-xl text-black">
              <h2 className="text-3xl font-bold text-blue-700 mb-4">{scenario.title || 'Challenge Briefing'}</h2>
              <p className="text-gray-800 text-lg leading-relaxed mb-6">
                {scenario.description || 'No description provided for this challenge.'}
              </p>
              <button
                onClick={() => setShowChallengeBriefing(false)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
              >
                Start Challenge
              </button>
            </div>
          </div>
        )} */}
        {/* NOTIFICATION POPUPS */}
        {notifications.length > 0 && (
          <div className="absolute bottom-14 right-4 z-[10002] space-y-2">
            {notifications.map((notif) => (
              <NotificationPopup
                key={notif.id}
                notification={notif}
                onClose={() => setNotifications(n => n.filter(i => i.id !== notif.id))}
              />
            ))}
          </div>
        )}

        {/* Simulation Exit Button */}
        {/* {fullScreen && (
          <button
            onClick={onExit}
            className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded shadow-lg z-[9999] flex items-center space-x-2"
          >
            <X size={16} />
            <span>Exit Simulation</span>
          </button>
        )} */}

        {/* Task completion overlay */}
        {scenario?.showCompletionPrompt && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full mx-0 sm:mx-4">
              <h3 className="text-xl font-bold mb-4">Task Complete</h3>
              <p className="text-gray-600 mb-6">{scenario.completionMessage}</p>
              <div className="space-y-3">
                <button
                  onClick={handleLearnMore}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
                >
                  <BookOpen size={16} />
                  Learn More
                </button>
                <button
                  onClick={() => onComplete && onComplete({ success: true, timeElapsed: 300 })}
                  className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                >
                  Continue
                </button>
                <button
                  onClick={onExit}
                  className="w-full bg-gray-300 text-gray-800 py-2 rounded hover:bg-gray-400"
                >
                  Exit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RealisticOSSimulator;